import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import SEO from '@/components/common/SEO';
import { AdUnit } from '@/components/common';
import { getAuthor } from './authors';
import { AuthorByline, EEATBlock } from './AuthorByline';
import { TableOfContents, FAQ } from './Blocks';
import ObangsaekStripe from './ObangsaekStripe';
import type { GuideMeta } from '@/content/guides/types';
import { GUIDE_CATEGORIES, categoryVars } from '@/content/guides/types';
import { GUIDES_BY_SLUG } from '@/content/guides/registry';

const BASE_URL = 'https://koreanhub.net';

/**
 * Long-form article shell.
 *
 * Layout is a single 672px reading column (DESIGN.md max-w-2xl), with the TOC
 * floated into the left margin only at xl where there is genuine room for it —
 * never as a sidebar that steals width from the text.
 */
export default function ArticleLayout({ guide, children }: { guide: GuideMeta; children: ReactNode }) {
  const author = getAuthor(guide.authorId);
  const category = GUIDE_CATEGORIES[guide.category];
  const url = `${BASE_URL}/guides/${guide.slug}`;

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: guide.title,
    description: guide.description,
    inLanguage: 'en-US',
    datePublished: guide.publishedAt,
    dateModified: guide.updatedAt,
    mainEntityOfPage: { '@type': 'WebPage', '@id': url },
    author: {
      '@type': 'Person',
      name: author.name,
      jobTitle: author.title,
      description: author.bio,
      url: `${BASE_URL}${author.profileUrl}`,
      ...(author.sameAs?.length ? { sameAs: author.sameAs } : {}),
    },
    publisher: {
      '@type': 'Organization',
      name: 'KoreanHub',
      url: BASE_URL,
      logo: { '@type': 'ImageObject', url: `${BASE_URL}/logo-koreanhub.svg` },
    },
    ...(guide.reviewer
      ? { reviewedBy: { '@type': 'Person', name: guide.reviewer.name, jobTitle: guide.reviewer.title } }
      : {}),
    keywords: guide.keywords.join(', '),
    articleSection: category.label,
    citation: guide.sources.map((s) => ({ '@type': 'CreativeWork', name: s.label, url: s.url })),
  };

  const faqSchema =
    guide.faq.length > 0
      ? {
          '@context': 'https://schema.org',
          '@type': 'FAQPage',
          mainEntity: guide.faq.map((f) => ({
            '@type': 'Question',
            name: f.question,
            acceptedAnswer: { '@type': 'Answer', text: f.answer },
          })),
        }
      : null;

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: BASE_URL },
      { '@type': 'ListItem', position: 2, name: 'Guides', item: `${BASE_URL}/guides` },
      {
        '@type': 'ListItem',
        position: 3,
        name: category.label,
        item: `${BASE_URL}/guides?category=${guide.category}`,
      },
      { '@type': 'ListItem', position: 4, name: guide.shortTitle, item: url },
    ],
  };

  const related = guide.related.map((s) => GUIDES_BY_SLUG[s]).filter(Boolean);

  return (
    <article className="bg-white dark:bg-surface-dark">
      <SEO
        title={guide.title}
        description={guide.description}
        path={`/guides/${guide.slug}`}
        type="article"
        jsonLd={faqSchema ? [articleSchema, faqSchema, breadcrumbSchema] : [articleSchema, breadcrumbSchema]}
      />

      {/* ── Header ── */}
      <header className="border-b border-ink-200 bg-ink-50 dark:border-surface-dark-border dark:bg-white/[0.02]">
        <div className="mx-auto max-w-article px-4 pb-8 pt-8 sm:px-6">
          {/* Breadcrumb */}
          <nav aria-label="Breadcrumb" className="mb-5">
            <ol className="flex flex-wrap items-center gap-1 text-xs text-ink-500 dark:text-ink-400">
              <li>
                <Link to="/" className="hover:text-ink-900 dark:hover:text-ink-50">
                  Home
                </Link>
              </li>
              <ChevronRight className="h-3 w-3" aria-hidden="true" />
              <li>
                <Link to="/guides" className="hover:text-ink-900 dark:hover:text-ink-50">
                  Guides
                </Link>
              </li>
              <ChevronRight className="h-3 w-3" aria-hidden="true" />
              <li>
                <Link
                  to={`/guides?category=${guide.category}`}
                  className="hover:text-ink-900 dark:hover:text-ink-50"
                >
                  {category.label}
                </Link>
              </li>
            </ol>
          </nav>

          <span
            className="inline-flex items-center rounded-full bg-[var(--cat-color)] px-2.5 py-1 text-xs font-semibold text-[var(--cat-on)]"
            style={categoryVars(category)}
          >
            {category.label}
          </span>

          <h1 className="mt-4 text-3xl font-bold leading-tight text-ink-900 dark:text-ink-50 sm:text-4xl">
            {guide.title}
          </h1>
          <p
            lang="ko"
            className="mt-3 font-korean text-lg font-medium leading-relaxed text-ink-500 dark:text-ink-400"
          >
            {guide.titleKo}
          </p>

          <AuthorByline
            className="mt-6"
            author={author}
            updatedAt={guide.updatedAt}
            readingTimeMinutes={guide.readingTimeMinutes}
            factChecked={guide.factChecked}
          />
        </div>
      </header>

      <ObangsaekStripe />

      {/* ── Body ── */}
      <div className="mx-auto max-w-article px-4 py-10 sm:px-6">
        {/* TOC: inline on mobile/tablet, floated into the margin at xl */}
        <TableOfContents
          items={guide.toc}
          className="mb-10 xl:sticky xl:top-24 xl:float-left xl:-ml-[19rem] xl:mb-0 xl:w-64"
        />

        <div className="xl:clear-none">{children}</div>

        {guide.faq.length > 0 && <FAQ items={guide.faq} />}

        {/* In-content ad — placed after substantive content, never before it */}
        <div className="my-10">
          <AdUnit slot="4471182390" format="horizontal" />
        </div>

        <EEATBlock
          author={author}
          reviewer={guide.reviewer}
          publishedAt={guide.publishedAt}
          updatedAt={guide.updatedAt}
          lastVerifiedAt={guide.lastVerifiedAt}
          factChecked={guide.factChecked}
          sources={guide.sources}
          disclaimer={guide.disclaimer}
        />

        {/* ── Related ── */}
        {related.length > 0 && (
          <section className="mt-12">
            <h2 className="mb-4 text-lg font-bold text-ink-900 dark:text-ink-50">
              Keep reading
              <span lang="ko" className="ml-2 font-korean text-sm font-medium text-ink-500 dark:text-ink-400">
                이어서 읽기
              </span>
            </h2>
            <div className="space-y-3">
              {related.map((r) => (
                <Link
                  key={r.slug}
                  to={`/guides/${r.slug}`}
                  style={categoryVars(GUIDE_CATEGORIES[r.category])}
                  className="block rounded-xl border border-ink-200 bg-white p-4 shadow-sm transition-shadow duration-200 hover:shadow-md dark:border-surface-dark-border dark:bg-surface-dark-card"
                >
                  <span className="text-[11px] font-semibold uppercase tracking-wide text-[var(--cat-text)] dark:text-[var(--cat-text-dark)]">
                    {GUIDE_CATEGORIES[r.category].label}
                  </span>
                  <h3 className="mt-1 text-[15px] font-semibold leading-snug text-ink-900 dark:text-ink-50">
                    {r.shortTitle}
                  </h3>
                  <p className="mt-1 line-clamp-2 text-sm leading-6 text-ink-500 dark:text-ink-400">
                    {r.description}
                  </p>
                </Link>
              ))}
            </div>
          </section>
        )}
      </div>
    </article>
  );
}
